import React from "react";
import { ReactComponent as ChevronRight } from "../../assets/icons/chevron-right.svg";

interface SettingsRowProps {
  title: string;
  description?: React.ReactNode;
  /** Leading glyph. Rendered in the shared tile unless `leading` replaces it wholesale. */
  icon?: React.ReactNode;
  /** Replaces the icon tile entirely — an organization's avatar, for example. */
  leading?: React.ReactNode;
  /** Renders the row as a button. Omit for a static or label row. */
  onClick?: () => void;
  /** Turns the row into a <label>, so a trailing control toggles from anywhere on the row. */
  htmlFor?: string;
  /** Replaces the chevron, e.g. with a segmented on/off control. */
  trailing?: React.ReactNode;
  /** Rotates the chevron down — for a row that expands in place. */
  expanded?: boolean;
  /** Drops a wide trailing control onto its own line below sm, so it stops crushing the copy. */
  trailingBelowSm?: boolean;
  /** Body revealed under the row, inside the same list card, on the sunken ground. */
  children?: React.ReactNode;
  disabled?: boolean;
}

// One shape for every settings entry, so the account, privacy, organization and admin rows can't drift apart. A row is a list item inside a SettingsGroup's card — it carries no frame of its own, which is what keeps the page off the card-inside-a-card look.
const SettingsRow: React.FC<SettingsRowProps> = ({
  title,
  description,
  icon,
  leading,
  onClick,
  htmlFor,
  trailing,
  expanded,
  trailingBelowSm = false,
  children,
  disabled = false,
}) => {
  const interactive = !!onClick || !!htmlFor;
  const Tag: any = onClick ? "button" : htmlFor ? "label" : "div";

  return (
    <>
      <Tag
        {...(onClick ? { type: "button", onClick, disabled } : {})}
        {...(htmlFor ? { htmlFor } : {})}
        className={`group flex w-full gap-4 px-5 py-4 text-left transition-colors ${
          trailingBelowSm
            ? "flex-col items-start sm:flex-row sm:items-center"
            : "items-center"
        } ${
          interactive && !disabled
            ? "cursor-pointer hover:bg-eai-surface-hover active:bg-eai-surface-active"
            : ""
        } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
      >
        {/* Glyph and copy stay one unit, so a stacked trailing control drops below both */}
        <div className="flex w-full min-w-0 flex-1 items-center gap-4">
          {leading ??
            (icon && (
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-eai-card border border-eai-border bg-eai-surface-sunken text-eai-teal-deep transition-colors group-hover:border-eai-border-hover">
                {icon}
              </span>
            ))}
          <div className="min-w-0 flex-1">
            <p className="eai-card-title">{title}</p>
            {description && <p className="eai-description-sm mt-0.5">{description}</p>}
          </div>
        </div>
        {trailing ??
          (interactive && (
            // The file hardcodes its own stroke, so currentColor has to be forced in
            <ChevronRight
              aria-hidden
              className={`h-4 w-4 shrink-0 text-eai-ink-faint transition-transform [&_path]:stroke-current group-hover:text-eai-ink-muted ${
                expanded ? "rotate-90" : ""
              }`}
            />
          ))}
      </Tag>
      {expanded && children && (
        <div className="bg-eai-surface-sunken px-5 py-4">{children}</div>
      )}
    </>
  );
};

// A titled group of rows: the heading sits on the page ground and the rows share one card, separated by hairlines — so the page has two levels, not three stacked white surfaces.
export const SettingsGroup: React.FC<{
  title: string;
  children: React.ReactNode;
  className?: string;
}> = ({ title, children, className = "" }) => (
  <section className={className}>
    <h2 className="eai-panel-title">{title}</h2>
    <div className="eai-card mt-3 divide-y divide-eai-border overflow-hidden">{children}</div>
  </section>
);

export default SettingsRow;
